import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteRagReport } from '../../types/siteRag';
import type { PathToGoDecisionEvidenceSnapshot } from '../../types/pathToGoDecision';
import { evaluateReportSemanticReadiness } from '../decisionAnalysis/readiness';
import { deriveScreeningArchitectureRecommendations } from '../findings/screeningArchitectureRecommendations';
import { canonicalJson, hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { hashProjectIntent } from '../sed/shouldBootstrap';
import { reportCanContributeSavedResult, reportCompletesScreening } from './reportPlan';

export function screeningPositiveDecisionAllowed(evidence: PathToGoDecisionEvidenceSnapshot): boolean {
  return evidence.decisionStage === 1
    && evidence.screeningPositiveDecisionGateSatisfied === true
    && evidence.screening?.report != null
    && evidence.screening.reportContentHash != null
    && evidence.screening.sourceHash != null;
}

function formatReportVersionLabel(report: SiteRagReport): string {
  const major = typeof report.version === 'number' && Number.isInteger(report.version) && report.version > 0
    ? report.version
    : 1;
  const minor = typeof report.minorVersion === 'number' && Number.isInteger(report.minorVersion) && report.minorVersion >= 0
    ? report.minorVersion
    : 0;
  return `v${major}.${minor}`;
}

function buildScreeningDecisionLockReason(input: {
  site: SiteEntity;
  sed: SiteEvidenceDocument | null;
  reports: readonly SiteRagReport[];
  analysisGateEnabled?: boolean;
}): string {
  const base = 'A current, eligible saved Screening Assessment bound to this site, study area and SED is required for PROCEED or PROCEED WITH CONCERNS.';
  const { site, sed } = input;
  const siteFeasibilityReports = input.reports
    .filter(report => report.siteId === site.id && (report.reportType ?? 'feasibility') === 'feasibility')
    .sort((a, b) => {
      const time = (r: SiteRagReport) => {
        const value = Date.parse(r.updatedAt ?? r.createdAt);
        return Number.isFinite(value) ? value : 0;
      };
      return time(b) - time(a) || a.id.localeCompare(b.id);
    });
  if (siteFeasibilityReports.length === 0) return base;

  const details: string[] = [];
  for (let i = 0; i < Math.min(siteFeasibilityReports.length, 2); i++) {
    const candidate = siteFeasibilityReports[i]!;
    const prefix = i === 0 ? 'Latest Screening Assessment' : 'Earlier Screening Assessment';
    const label = `${prefix} ${formatReportVersionLabel(candidate)}`;
    const semanticAdmission = evaluateReportSemanticReadiness(candidate);
    if (candidate._metadataOnly || typeof candidate.summary !== 'string') {
      details.push(`${label} has only list metadata loaded (full report body unavailable).`);
    } else if (
      semanticAdmission.holdReason != null
      || candidate.qualityHold === 'needs-revision'
      || (candidate.qualityHoldFindings?.length ?? 0) > 0
    ) {
      const findingsCount = candidate.qualityHoldFindings?.length ?? 0;
      const detail = findingsCount > 0
        ? `${findingsCount} blocking quality or grounding ${findingsCount === 1 ? 'finding requires' : 'findings require'} revision`
        : (semanticAdmission.holdReason?.message ?? 'blocking quality or grounding findings require revision');
      details.push(`${label} is held for quality/grounding revision (${detail.replace(/\.+$/, '')}).`);
    } else if (!reportCanContributeSavedResult(candidate, 'feasibility')) {
      details.push(`${label} failed immutable revision or body readiness validation.`);
    } else if (!site.studyAreaScope || !candidate.studyAreaScope || !sed?.studyAreaScope
      || canonicalJson(candidate.studyAreaScope) !== canonicalJson(site.studyAreaScope)
      || canonicalJson(sed.studyAreaScope) !== canonicalJson(site.studyAreaScope)) {
      details.push(`${label} does not match the current study area scope.`);
    } else if (
      !sed || sed.id !== site.sedId || sed.version !== site.sedVersion
      || candidate.sedId !== site.sedId || candidate.sedVersionUsed !== site.sedVersion
      || semanticAdmission.stale
    ) {
      const usedLabel = candidate.sedVersionUsed != null ? `Report used SED v${candidate.sedVersionUsed}` : 'Report SED version not recorded';
      const currentLabel = site.sedVersion != null ? `Current SED v${site.sedVersion}` : 'current SED';
      details.push(`${label} is stale against ${currentLabel} (${usedLabel}).`);
    } else if (!reportCompletesScreening(candidate, 'feasibility', {
      site, sed, intent: site.projectIntent,
      requiresGenerationImpact: false, analysisGateEnabled: input.analysisGateEnabled,
    })) {
      details.push(`${label} is no longer current against the latest project intent or screening criteria.`);
    }
  }
  return details.length > 0 ? `${base} ${details.join(' ')}` : base;
}

/** A conditional screening judgment, never the final GO gate or an Intent choice.
 * Uses the existing finalized-body/currentness/quality predicate and then binds
 * the selected geometry and current SED explicitly. Missing proof stays unknown. */
export function buildScreeningDecisionEvidenceSnapshot(input: {
  site: SiteEntity;
  sed: SiteEvidenceDocument | null;
  reports: readonly SiteRagReport[];
  capturedAt?: string;
  analysisGateEnabled?: boolean;
}): PathToGoDecisionEvidenceSnapshot {
  const { site, sed } = input;
  const candidates = input.reports.filter(report => {
    if (!reportCanContributeSavedResult(report, 'feasibility')
      || report.siteId !== site.id || !site.studyAreaScope || !report.studyAreaScope
      || !sed?.studyAreaScope || sed.id !== site.sedId
      || sed.version !== site.sedVersion
      || canonicalJson(report.studyAreaScope) !== canonicalJson(site.studyAreaScope)
      || canonicalJson(sed.studyAreaScope) !== canonicalJson(site.studyAreaScope)
      || report.qualityHold === 'needs-revision' || (report.qualityHoldFindings?.length ?? 0) > 0
      || !evaluateReportSemanticReadiness(report).admittedForCurrentDecision) return false;
    return reportCompletesScreening(report, 'feasibility', {
      site, sed, intent: site.projectIntent,
      requiresGenerationImpact: false, analysisGateEnabled: input.analysisGateEnabled,
    });
  }).sort((a, b) => {
    const time = (r: SiteRagReport) => {
      const value = Date.parse(r.updatedAt ?? r.createdAt);
      return Number.isFinite(value) ? value : 0;
    };
    return time(b) - time(a) || a.id.localeCompare(b.id);
  });
  const report = candidates[0] ?? null;
  const snapshot = report ? {
    id: report.id, reportType: 'feasibility' as const,
    version: report.version ?? 1, minorVersion: report.minorVersion ?? 0,
    createdAt: report.createdAt,
    ...(report.sedId ? { sedId: report.sedId } : {}),
    ...(report.sedVersionUsed != null ? { sedVersionUsed: report.sedVersionUsed } : {}),
  } : null;
  const reason = report ? null : buildScreeningDecisionLockReason(input);
  return {
    decisionStage: 1,
    ...(site.demonstrationProvenance ? { decisionScope: {
      kind: 'hypothetical-scenario' as const,
      scenarioId: site.demonstrationProvenance.scenarioId,
      realWorldEligible: false as const,
    } } : {}),
    capturedAt: input.capturedAt ?? new Date().toISOString(),
    intentHash: hashProjectIntent(site.projectIntent ?? null),
    projectIntent: site.projectIntent ? structuredClone(site.projectIntent) : null,
    sedId: site.sedId ?? null, sedVersion: site.sedVersion ?? null,
    generationImpactApplicability: 'undecided', btmStrategy: null, strategyDeclared: false,
    requiredReportTypes: ['feasibility'],
    // This remains false: a Stage 1 decision can never authorize final GO.
    positiveDecisionGateSatisfied: false,
    screeningPositiveDecisionGateSatisfied: report != null,
    system: { band: 'withheld', label: report ? 'Conditional screening evidence available' : 'Screening evidence awaiting review',
      composite: null, confidenceTag: null, dataAdequate: report != null,
      adequacyReason: reason, coverageRatio: 0 },
    reports: snapshot ? [snapshot] : [],
    screening: { schemaVersion: 1, report: snapshot,
      reportContentHash: report ? hashCanonicalJson(report) : null,
      sourceHash: report?.reportRevision?.sourceHash ?? null,
      studyAreaScope: site.studyAreaScope ? structuredClone(site.studyAreaScope) : null,
      recommendations: report ? structuredClone(deriveScreeningArchitectureRecommendations(report, site.id)) : {},
      reason },
  };
}
